import {
  ChangeDetectionStrategy,
  Component,
  ViewChild,
  ViewEncapsulation,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Option } from '@interfaces/common/option';
import { PartnerAssetUpdateRequest } from '@interfaces/partner-income-tax/requests/partner-income-tax.request';
import {
  AssetEligibility,
  PartnerAssetDto,
  ValueSource,
} from '@interfaces/partner-income-tax/responses/partner-income-tax.response';
import { TranslateService } from '@ngx-translate/core';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { VaultBaseComponent } from '@shared/components/base/vault-base.component';
import { DialogComponent } from '@shared/components/dialog/dialog.component';
import { SharedModule } from '@shared/shared.module';
import { first } from 'rxjs';
import { PartnerIncomeTaxStateService } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/partner-income-tax-state.service';
import {
  ELIGIBILITY_TAGS,
  formatAssetName,
} from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/utils/partner-income-tax-format.util';

/** Nome curto: cabe numa linha da tabela de bens. */
const SHORT_NAME_MAX = 60;

const SOURCE_OPTIONS: Option[] = [
  { label: 'Laudo de avaliação', value: 'laudo' },
  { label: 'Extrato', value: 'extrato' },
  { label: 'Declarado no IR', value: 'declarado' },
  { label: 'Estimado pelo analista', value: 'analista' },
];

const ELIGIBILITY_OPTIONS: Option[] = (
  ['elegivel', 'restrito', 'inelegivel', 'vendido'] as AssetEligibility[]
).map(value => ({ label: ELIGIBILITY_TAGS[value].label, value }));

/** Linhas de texto (uma restrição por linha) para a lista do contrato. */
export function parseRestrictions(text: string | null | undefined): string[] {
  return (text ?? '')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);
}

/**
 * Edição do bem pelo analista (exige `PARTNERINCOMETAX_UPDATE`): valor de referência e origem, data, situação,
 * restrições e valor já comprometido. Valores em R$ mil, como a tabela. "Ofertado nesta operação" saiu da tela
 * (decisão do Victor, 28/10/2026): o PUT não manda mais `offeredInOperation`, e o backend mantém o que está gravado.
 */
@Component({
  selector: 'app-pit-asset-edit-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, SharedModule],
  templateUrl: './asset-edit-dialog.component.html',
  styleUrl: './asset-edit-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class AssetEditDialogComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  private readonly state = inject(PartnerIncomeTaxStateService);
  private readonly dates = inject(DateFormatterService);
  private readonly fb = inject(FormBuilder);

  protected readonly asset = signal<PartnerAssetDto | null>(null);
  protected readonly submitting = signal(false);
  protected readonly sourceOptions = SOURCE_OPTIONS;
  protected readonly eligibilityOptions = ELIGIBILITY_OPTIONS;
  protected readonly shortNameMax = SHORT_NAME_MAX;
  protected readonly shortNameInputId = 'pit-asset-short-name';

  readonly form = this.fb.group({
    shortName: [''],
    referenceValueThousands: [null as number | null],
    referenceSource: [null as string | null],
    referenceDate: [null as Date | string | null],
    eligibility: ['elegivel' as string],
    restrictions: [''],
    committedThousands: [null as number | null],
  });

  constructor() {
    super(inject(TranslateService));
  }

  open(asset: PartnerAssetDto): void {
    this.asset.set(asset);
    this.form.reset({
      // Na tela o nome aparece em caixa de frase; o campo começa igual ao que a tabela mostra.
      shortName: formatAssetName(asset.shortName),
      referenceValueThousands:
        asset.referenceValue == null ? null : Math.round(asset.referenceValue / 1_000),
      referenceSource: asset.referenceSource,
      referenceDate: asset.referenceDate
        ? (this.dates.parseDate(asset.referenceDate) ?? null)
        : null,
      eligibility: asset.eligibility,
      restrictions: asset.restrictions.join('\n'),
      committedThousands: asset.committed ? Math.round(asset.committed / 1_000) : null,
    });
    this.dialogRef?.open('', '', '', 'Cancelar', '', 'pit-asset-edit', false, false);
    this.showShortNameStart();
  }

  protected cancel(): void {
    this.dialogRef?.close();
  }

  /** Tamanho do nome curto digitado, para o contador. */
  protected shortNameLength(): number {
    return (this.form.controls.shortName.value ?? '').trim().length;
  }

  /**
   * Se o campo ganhar o foco ao abrir, o navegador mostra o FIM do nome ("...SITUADO NA RUA") e parece outro bem.
   * Cursor e rolagem voltam para o início.
   */
  private showShortNameStart(): void {
    setTimeout(() => {
      const field = document.getElementById(this.shortNameInputId) as HTMLInputElement | null;
      if (!field || typeof field.setSelectionRange !== 'function') return;
      field.setSelectionRange(0, 0);
      field.scrollLeft = 0;
    });
  }

  /**
   * A pessoa mexeu no nome. Só aí o tamanho vale: nome longo herdado da declaração não trava quem abriu a modal só
   * para trocar a Situação (R-G8); sem edição, o nome original é gravado como veio.
   */
  protected shortNameEdited(): boolean {
    return this.form.controls.shortName.dirty;
  }

  /** Nome editado acima do limite: contador vermelho e Salvar bloqueado. */
  protected shortNameTooLong(): boolean {
    return this.shortNameEdited() && this.shortNameLength() > SHORT_NAME_MAX;
  }

  isFormValid(): boolean {
    const v = this.form.getRawValue();
    const nonNegative = (n: number | null) => n == null || `${n}` === '' || Number(n) >= 0;
    const name = v.shortName?.trim() ?? '';
    return (
      !!name &&
      !this.shortNameTooLong() &&
      nonNegative(v.referenceValueThousands) &&
      nonNegative(v.committedThousands)
    );
  }

  submitAction(): void {
    const asset = this.asset();
    if (!asset || !this.isFormValid() || this.submitting()) return;

    const v = this.form.getRawValue();
    const reais = (n: number | null) => (n == null || `${n}` === '' ? null : Number(n) * 1_000);
    const typedName = v.shortName?.trim() || null;
    const request: PartnerAssetUpdateRequest = {
      // Nome igual ao que a tela mostrou (só a caixa mudou): grava o original, para não virar "ajuste do analista".
      shortName: typedName === formatAssetName(asset.shortName) ? asset.shortName : typedName,
      referenceValue: reais(v.referenceValueThousands),
      referenceSource: (v.referenceSource as ValueSource | null) ?? null,
      referenceDate: v.referenceDate ? this.dates.formatDateToYYYYMMDD(v.referenceDate) : null,
      eligibility: (v.eligibility as AssetEligibility | null) ?? null,
      restrictions: parseRestrictions(v.restrictions),
      committedAmount: reais(v.committedThousands),
    };

    this.submitting.set(true);
    this.state
      .updateAsset(asset.id, request)
      .pipe(first())
      .subscribe(ok => {
        this.submitting.set(false);
        if (ok) this.dialogRef?.close();
      });
  }
}
